import React, { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useModalContext } from '../../../../context/Modal';
import { DropdownMenu } from '../../../form';
import { Icon } from '../../../UI';
import WindowWidget from '../../WindowWidget';
import {
  LIST_ASSET_ALERTS,
  SEARCH_ASSET_ALERTS,
} from '../../../../http/apollo-queries';
import { getLogo } from '../../../../helpers/functions';
import EditAlert from './EditAlert';
import { Loading } from '../../../UI';
import DeleteModal from './DeleteModal';
import { IAssetAlert } from '../../../../types/model';
import { useSidebar } from '../../../../context/Sidebar';

interface ManageAlertsProps {
  goBack: () => void;
}

const ManageAlerts: React.FC<ManageAlertsProps> = ({ goBack }) => {
  // State
  const [searchResults, setSearchResults] = useState<IAssetAlert[] | null>(
    null
  );

  // Hooks
  const { setIsOpen } = useSidebar();
  const { dispatch: dispatchModal } = useModalContext();
  const { t } = useTranslation();

  // Queries
  const {
    data: { listAssetAlerts: { items: smartAlertsList = [] } = {} } = {},
    loading,
  } = useQuery<{
    listAssetAlerts: { items: IAssetAlert[] };
  }>(LIST_ASSET_ALERTS, { fetchPolicy: 'cache-and-network' });

  const [searchAlerts, { loading: loadingSearch }] = useLazyQuery(
    SEARCH_ASSET_ALERTS,
    {
      onCompleted: (data) => {
        setSearchResults(data?.searchAssetAlerts?.items || null);
      },
    }
  );

  const search = useCallback(
    debounce((event) => {
      if (!event.target.value) return setSearchResults(null);
      return searchAlerts({ variables: { term: event.target.value } });
    }, 500),
    []
  );

  return (
    <WindowWidget
      title={t('alerts.manageSmartAlerts')}
      onChange={search}
      actions={[
        <div key="1">
          <DropdownMenu
            dropdownItems={[
              <span
                onClick={() =>
                  dispatchModal({
                    className: 'justify-center text-center items-center',
                    element: <DeleteModal />,
                  })
                }
              >
                {t('alerts.deleteAll')}
              </span>,
            ]}
            dropdownTooltipClassName="bg-white border-solid border-grey-10 border shadow-md"
          >
            <Icon svgName="options" className="cursor-pointer" />
          </DropdownMenu>
        </div>,
        <Icon
          svgName="close"
          className="h-3 cursor-pointer"
          onClick={() => setIsOpen(false)}
          key="2"
        />,
      ]}
    >
      {loading || loadingSearch ? (
        <div className={'flex w-full justify-center mt-2'}>
          <Loading status={true} size={'small'} />{' '}
        </div>
      ) : (
        <>
          <div className={'flex justify-center w-full'}></div>
          <div
            className="flex gap-3 items-center py-3 px-6 border-b border-grey-10 cursor-pointer dark:border-grey-80"
            onClick={goBack}
          >
            <Icon svgName="arrowBack" />
            <p className="font-medium">{t('alerts.notifications')}</p>
          </div>
          {(searchResults ?? smartAlertsList).map((smartAlert) => {
            const { id, name, asset } = smartAlert;
            const { type, logo } = asset;
            return (
              <div
                className="flex justify-between group px-4 py-2 cursor-pointer hover:bg-primary hover:text-black rounded-xl m-2"
                key={id}
              >
                <div className="flex gap-3">
                  {/* Icon */}
                  <div className="w-6 h-6">
                    <img
                      loading="lazy"
                      src={getLogo(type, logo)}
                      alt="Notification icon"
                      className="object-cover rounded-full w-6 h-6"
                    />
                  </div>
                  {/* Body */}
                  <div className="flex flex-col">
                    <h3 className="font-medium">
                      {name} | {asset.symbol}
                    </h3>
                  </div>
                </div>
                {/* Time */}

                <DropdownMenu
                  dropdownItems={[
                    <span
                      onClick={() =>
                        dispatchModal({
                          element: <EditAlert smartAlert={smartAlert} />,
                          title: t(`alerts.editAlert`),
                        })
                      }
                    >
                      {t('alerts.edit')}
                    </span>,
                    <span
                      onClick={() =>
                        dispatchModal({
                          className: 'justify-center text-center items-center',
                          element: <DeleteModal id={smartAlert.id} />,
                        })
                      }
                    >
                      {t('alerts.delete')}
                    </span>,
                  ]}
                  dropdownClassName="self-center"
                  dropdownTooltipClassName="bg-white border-solid border-grey-10 border shadow-md"
                >
                  <Icon
                    svgName="options"
                    className="self-center dark:group-hover:text-black"
                  />
                </DropdownMenu>
              </div>
            );
          })}
        </>
      )}
    </WindowWidget>
  );
};

export default ManageAlerts;
